import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { StatsBar, InsightList, WhyThisMatters } from '../components/Shared.jsx';

export function GuestExtractor({ data }) {
  const { guests = [], guestStats = {}, insights = [] } = data;

  return (
    <Box>
      {Object.keys(guestStats).length > 0 && (
        <StatsBar stats={{
          'Total Guests':  guestStats.uniqueGuests,
          'Appearances':   guestStats.totalAppearances,
          'Repeat Guests': guestStats.repeatGuests,
          'With Contact':  guestStats.contactInfoFound,
        }} />
      )}

      {guests.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Guests</Typography>
          {guests.map((g, i) => (
            <GuestCard key={i} guest={g} />
          ))}
        </Box>
      )}

      <InsightList insights={insights} />
      <WhyThisMatters text="Guest patterns reveal audience appeal and can uncover repeat booking opportunities." citations={data.expertCitations} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Guest card with expandable episode list
// ---------------------------------------------------------------------------
function GuestCard({ guest: g }) {
  const [expanded, setExpanded] = useState(false);
  const episodeList = g.episodes || [];

  return (
    <Box sx={{ mb: 1.5, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: T.text }}>{g.name}</Typography>
        <Typography sx={{ fontSize: 11, color: T.textSecondary }}>{g.appearances} appearance{g.appearances !== 1 ? 's' : ''}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{g.role}{g.organization ? ` · ${g.organization}` : ''}</Typography>

      {/* Episode list */}
      {episodeList.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Box
            component="button"
            onClick={() => setExpanded(!expanded)}
            sx={{
              background: 'none', border: 'none', color: T.accent, fontSize: 11,
              cursor: 'pointer', p: 0, textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {expanded ? '▾ Hide episodes' : `▸ Episodes (${episodeList.length})`}
          </Box>
          {expanded && (
            <Box sx={{ mt: 0.5, pl: 1, borderLeft: `2px solid ${T.border}` }}>
              {episodeList.map((ep, j) => (
                <Typography key={j} sx={{ fontSize: 11, color: T.textSecondary, mb: 0.25 }}>
                  {ep}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Contact info */}
      {(g.contactInfo?.email || g.contactInfo?.website || g.contactInfo?.social) && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {g.contactInfo.email && (
            <Typography component="a" href={`mailto:${g.contactInfo.email}`} sx={{ fontSize: 12, color: T.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {g.contactInfo.email}
            </Typography>
          )}
          {g.contactInfo.website && (
            <Typography component="a" href={g.contactInfo.website} target="_blank" rel="noreferrer" sx={{ fontSize: 12, color: T.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Website
            </Typography>
          )}
          {g.contactInfo.social && (
            <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{g.contactInfo.social}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
